/**
 * The Tribe container
 */
import { Medium } from "@bkry/bowline-components/Medium";
import { ScrollToTop as useScrollToTop } from "@bkry/bowline-components/Utils";
import { listPartnerDeals } from "@bkry/bowline-redux/partnerDeals";
import { listRaffles } from "@bkry/bowline-redux/raffles";
import { getTribeAttributes, showTribe } from "@bkry/bowline-redux/tribes";
import {
  listUnlockBoundaries,
  listUnlockBoundariesForUnlockable,
} from "@bkry/bowline-redux/unlockBoundaries";
import { listUnlockableContents } from "@bkry/bowline-redux/unlockableContents";
import { env } from "@bkry/bowline-utils";
import { useSsrEffect } from "@issr/core";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Col, Container, Row, Spinner } from "reactstrap";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs";
import Page from "../components/Page/Page-component";
import { PartnerDealPreview } from "../components/PartnerDeals";
import { RafflePreview } from "../components/Raffles";
import { UnlockableContentPreview } from "../components/UnlockableContent";
import TribeBoundaryCard from "../components/TribeBoundary/TribeBoundaryCard-component";
const { REACT_APP_PROJECT_ID } = env;
/**
 * Tribe container, the landing page for logged in users
 */
const Tribe = (_props, context) => {
  useScrollToTop();
  const dispatch = useDispatch();
  const { tribeId } = useParams();
  const tribeData = useSelector((state) => getTribeAttributes(state, tribeId));
  const signedIn = useSelector((state) => state.wallets.siweSuccess);

  const dispatchApi = async () => {
    dispatch(showTribe({ projectId: REACT_APP_PROJECT_ID, id: tribeId }));
    dispatch(
      listUnlockableContents({
        projectId: REACT_APP_PROJECT_ID,
        pageNum: 1,
        itemNum: 4,
        filters: {
          tribe_id: tribeId,
        },
      })
    );
    dispatch(
      listRaffles({
        projectId: REACT_APP_PROJECT_ID,
        pageNum: 1,
        itemNum: 4,
        filters: {
          tribe_id: tribeId,
        },
      })
    );
    dispatch(
      listPartnerDeals({
        projectId: REACT_APP_PROJECT_ID,
        pageNum: 1,
        itemNum: 4,
        filters: {
          tribe_id: tribeId,
        },
      })
    );
  };

  const loadingRaffles = useSelector((state) => state.raffles.loadingList);

  const loadingPartnerDeals = useSelector(
    (state) => state.partnerDeals.loadingList
  );

  const loadingUnlockableContents = useSelector(
    (state) => state.unlockableContents.loadingList
  );

  const loadingUnlockBoundaries = useSelector(
    (state) => state.unlockBoundaries.loadingList
  );

  // TODO just a helper for now
  const loadingAnyUnlockable =
    loadingRaffles || loadingPartnerDeals || loadingUnlockableContents;

  const unlockableContentIds = useSelector(
    (state) => state?.unlockableContents?.idsList
  );

  const raffleIds = useSelector((state) => state?.raffles?.idsList);

  const partnerDealIds = useSelector((state) => state?.partnerDeals?.idsList);

  useSsrEffect(dispatchApi);
  useEffect(dispatchApi, [dispatch, tribeId]);
  const error = useSelector((state) => state?.tribes?.showError);
  const lang = useSelector((state) => state.i18nState.lang);
  const tribeName = tribeData?.name || context.t("Tribe");
  console.log("errror", error);

  useEffect(() => {
    dispatch(
      listUnlockBoundaries({ projectId: REACT_APP_PROJECT_ID, tribeId })
    );
  }, [dispatch, tribeId]);

  const unlockBoundaries = useSelector(
    (state) =>
      listUnlockBoundariesForUnlockable(state, {
        id: tribeId,
        type: "tribe",
      }) || []
  );

  return (
    <Page id="TribeDetail" title={tribeName}>
      <Container>
        <Breadcrumbs
          breadcrumbs={[
            {
              target: `/${lang}/community/membershiptiers`,
              label: context.t("Community Membership Tiers"),
              active: false,
            },
            {
              target: `/${lang}/community/membershiptiers/${tribeId}`,
              label: tribeData?.name || context.t("Membership Tier"),
              active: true,
            },
          ]}
        />
      </Container>
      <Container>
        <Row>
          <Col md="6">
            <h1>{tribeName}</h1>
            {tribeData?.description && (
              <p dangerouslySetInnerHTML={{ __html: tribeData?.description }} />
            )}
          </Col>
          <Col md="6">
            {tribeData?.public_medium_original &&
              tribeData?.public_medium_metadata && (
                <Medium
                  mediumData={{
                    ...tribeData?.public_medium_original,
                    ...tribeData?.public_medium_metadata,
                  }}
                  className="justify-content-center mb-3"
                  altTitle={tribeName}
                  skipLazyLoading
                  autoPlay
                  loop
                />
              )}
          </Col>
        </Row>

        <section className="bigger-margin-top bigger-margin-bottom">
          <h2>{context.t("Required Collectibles")}</h2>
          {loadingUnlockBoundaries && !unlockBoundaries.length && (
            <div className="my-5 text-center">
              <Spinner
                style={{
                  height: "12rem",
                  width: "12rem",
                  "--bs-spinner-animation-speed": "5s",
                }}
              />
            </div>
          )}
          {!!unlockBoundaries.length && (
            <Row>
              {unlockBoundaries?.map((unlockBoundary, index) => (
                <Col key={index} className="my-2" md={3} xs={6}>
                  <TribeBoundaryCard unlockBoundaryId={unlockBoundary.id} />
                </Col>
              ))}
            </Row>
          )}
          {!signedIn && (
            <p>{context.t("To view your available tokens, please sign in.")}</p>
          )}
        </section>

        <section className="bigger-margin-top bigger-margin-bottom">
          <h2>{context.t("Accessible Benefits for this Membership Tier")}</h2>
          {loadingAnyUnlockable ? (
            <div className="my-5 text-center">
              <Spinner
                style={{
                  height: "12rem",
                  width: "12rem",
                  "--bs-spinner-animation-speed": "5s",
                }}
              />
            </div>
          ) : (
            <Row className="my-5">
              {raffleIds.length +
                partnerDealIds.length +
                unlockableContentIds.length ===
                0 && (
                <Col className="text-center py-5">
                  <p>
                    {context.t(
                      "Currently no benefits available for this tier."
                    )}
                  </p>
                </Col>
              )}
              {raffleIds?.map((raffleId) => (
                <Col
                  xs={6}
                  md={3}
                  key={raffleId}
                  style={{ marginBottom: "var(--bs-gutter-x)" }}
                >
                  <RafflePreview raffleId={raffleId} />
                </Col>
              ))}
              {partnerDealIds?.map((partnerDealId) => (
                <Col
                  xs={6}
                  md={3}
                  key={partnerDealId}
                  style={{ marginBottom: "var(--bs-gutter-x)" }}
                >
                  <PartnerDealPreview partnerDealId={partnerDealId} />
                </Col>
              ))}
              {unlockableContentIds?.map((unlockableContentId) => (
                <Col
                  xs={6}
                  md={3}
                  key={unlockableContentId}
                  style={{ marginBottom: "var(--bs-gutter-x)" }}
                >
                  <UnlockableContentPreview
                    unlockableContentId={unlockableContentId}
                  />
                </Col>
              ))}
            </Row>
          )}
        </section>
      </Container>
    </Page>
  );
};

/**  define proptype for the 'translation' function  */
Tribe.contextTypes = {
  t: PropTypes.func,
};

/** export the component as redux connected component */
export default Tribe;
